<script lang="ts">
	import { onMount } from "svelte";
	import Inspector from "./inspector/Inspector.svelte";
	import SceneManager, { WallConfig } from "./scene-manager";
	import Stage from "./stage/Stage.svelte";
	import { Button, Modal, NumberInput, TileGroup, RadioTile, ProgressBar, ToastNotification, Select, SelectItem, TextInput, Checkbox, FormGroup } from "carbon-components-svelte";
	import NewTab16 from "carbon-icons-svelte/lib/NewTab16"
	import Help16 from "carbon-icons-svelte/lib/Help16"
	import GroupObjectsSave16 from "carbon-icons-svelte/lib/GroupObjectsSave16"
	import { upload } from "./helpers";
	import ProgressModal from "./modals/progress-modal.svelte";
	import WelcomeModal from "./modals/welcome-modal.svelte";
	import Toasts from "./toasts.svelte";

	/* Localization */
	import { negotiateLanguages } from '@fluent/langneg';
	import { FluentBundle, FluentResource } from '@fluent/bundle';
	import { FluentProvider, Localized } from '@nubolab-ffwd/svelte-fluent';
	import deMessages from './i18n/de.ftl';
	import enMessages from './i18n/en.ftl';
	import ConfigurationModal from "./modals/configuration-modal.svelte";
	import SaveModal from "./modals/save-modal.svelte";

	const RESOURCES = {
		"de": new FluentResource(deMessages),
		"en": new FluentResource(enMessages),
	};

	function* generateBundles(userLocales) {
		// Choose locales that are best for the user.
		const currentLocales = negotiateLanguages(userLocales, ["de", "en"], {
			defaultLocale: 'de'
		});

		for (const locale of currentLocales) {
			const bundle = new FluentBundle(locale);
			bundle.addResource(RESOURCES[locale]);
			yield bundle;
		}
	}


	const manager = new SceneManager();

	let showBanner = false;
	let firstProject = true;
	let showHelp = false;
	let showNew = false;
	let showSave = false;
	let showProgress = false;
	let progressText = "status-saving"
	let progress = undefined;
	let newScene: SceneConfig = {
		width: 5,
		depth: 3,
		height: '',
		wall: WallConfig.U,
	}
	let toasts = [];
	let toSave = {
		firstName: "",
		lastName: "",
		email: "",
		newsletter: false,
		checked: false,
	}
	let languageCode = "de";
	$: canSave = !!toSave.firstName && !!toSave.lastName && !!toSave.email && toSave.checked;

	$: locale = (window.location.search.slice(1).split('&').map(p => p.split("=")).find(e => e[0] == "l") ?? [])[1];

	onMount(async () => {
		if (window.location.hash.length > 20) {
			await fetch(`/projects/${window.location.hash.slice(1)}/scene.json`)
				.then(r => r.json())
				.then(r => {
					manager.restore(r);
				});
		} else {
			showHelp = true;
		}

		const banner = await fetch("/banner/").then(r => r.text).catch(() => false);
		if (banner) {
			showBanner = true;
		}
	})

	function toggleShowHelp() {
		showHelp = !showHelp;
	}

	function toggleShowNew() {
		showNew = !showNew;
	}

	function toggleShowSave() {
		if (manager.id && manager.id === document.body.getAttribute('data-id')) {
			handleSave();
		} else {
			showSave = !showSave;
		}
	}

	function handleNew() {
		showNew = false;
		firstProject = false;
		window.location.hash = "";
		manager.clear();
		manager.setBounds(newScene.width, newScene.depth, parseInt(newScene.height), newScene.wall);
	}

	async function handleSave() {
		showSave = false;
		showProgress = true;
		progressText = "status-saving";
		progress = undefined;
		
		const result = await fetch("/save.php", {
			method: "POST",
			body: `{
				"scene": ${manager.serialize()},
				"contact": ${JSON.stringify(toSave)},
				"lang": "${languageCode}"
			}`,
			headers: {
				'Content-Type': 'application/json'
			},
		}).then(r => r.json())

		if(!result.error && result.id) {
			const { id } = result;
			window.location.hash = id;
			manager.id = id;
			document.body.setAttribute('data-id', id);
			if(manager.textures.size > 0) {
				let c = 1;
				for(let [k, t] of manager.textures) {
					if (typeof t !== "string") {
						progress = 0;
						progressText = languageCode == "en" ? `Saving Texture ${c} of ${manager.textures.size}.` : `Speichere Textur ${c} von ${manager.textures.size}.`;
						await upload(t, p => {
							progress = p;
						}, { id })
					}
				}
				addToast({
					kind: "success",
					title: "saving-success-title",
					subtitle: "saving-success-message"
				})
			} else {
				addToast({
					kind: "success",
					title: "saving-success-title",
					subtitle: "saving-success-message"
				})
				setTimeout(() => {
					showProgress = false;
				}, 1000);
				return;
			}
		} else {
			addToast({
				kind: "error",
				title: "saving-error-title",
				subtitle: "saving-error-message"
			})
		}

		showProgress = false;
	}

	function removeToast(t:any) {
		toasts = toasts.filter(tt => JSON.stringify(tt) !== JSON.stringify(t));
	}

	function addToast(t:any) {
		toasts = [...toasts, t];
	}
</script>

<FluentProvider bundles={generateBundles([locale, ...navigator.languages].filter(Boolean))}>
	<header>
		<img src="/img/logo.svg" alt="bee Logo" />
		{#if showBanner}<iframe class="banner" src="/banner" title="bee banner" />{/if}
		<div class="header-buttons">
			<Button icon={Help16} on:click={toggleShowHelp}><Localized id="header-help" /></Button>
			<Button icon={NewTab16} on:click={toggleShowNew}><Localized id="header-new" /></Button>
			<Button icon={GroupObjectsSave16} on:click={toggleShowSave}><Localized id="header-save" /></Button>
		</div>
	</header>

	<main>
		<Stage manager={manager} />
		<Inspector manager={manager} langCallback={l => languageCode = l} />
	</main>

	<Toasts {toasts} onRemove={removeToast} />

	<ProgressModal 
		{showProgress}
		{progress}
		{progressText}
	/>

	<WelcomeModal
		{showHelp}
		{firstProject}
		on:submit={() => {
			if(firstProject) {
				showNew = true;
			}
			showHelp = false;
		}}
	/>

	<ConfigurationModal 
		bind:open={showNew}
		on:click:button--secondary={toggleShowNew}
		on:submit={handleNew}
		bind:newScene={newScene}
	/>

	<SaveModal 
		canSave={canSave}
		bind:open={showSave}
		bind:toSave={toSave}
		on:click:button--secondary={toggleShowSave}
		on:submit={handleSave}
	/>

</FluentProvider>

<style type="text/scss" global>
	@use "carbon-components-svelte/css/white.css";

	$color-primary: #EF7C00;

	.bx--btn--primary {
		background-color: $color-primary;

		&:hover {
			background-color: darken($color-primary, 5%);
		}

		&:focus {
			border-color: $color-primary;
			box-shadow: inset 0 0 0 1px $color-primary, inset 0 0 0 2px #fff;
		}
	}

	.bx--btn--secondary {
		&:focus {
			border-color: $color-primary;
			box-shadow: inset 0 0 0 1px $color-primary, inset 0 0 0 2px #fff;
		}
	}

	.bx--btn--ghost {
		color: #000;
		&:hover {
			color: #000;
		}
		&:focus {
			border-color: $color-primary;
			box-shadow: inset 0 0 0 1px $color-primary, inset 0 0 0 2px #fff;
		}
	}

	.bx--modal-close:focus {
		border-color: $color-primary;
	}

	.bx--modal-container--xs {
		max-height: 80%;
	}

	.bx--modal-content {
		margin-bottom: 0;
	}

	.bx--tabs {
		flex-shrink: 0;
		flex-grow: 0;
	}

	.bx--tabs__nav {
		width: 100%;
		position: relative;
	}

	.bx--tabs__nav-item {
		.bx--tabs__nav-link {
			line-height: 1.29 !important;
			width: 100% !important;
			height: auto;

			&:active, &:focus {
				outline: 0;
				box-shadow: inset 0 2px 0 0 $color-primary !important;
			}
		}
	}
	
	.bx--tab-content {
		height: 100%;

		&:first-child {
			padding-bottom: 0;
		}
	}

	.bx--tabs__nav-item--selected:not(.bx--tabs__nav-item--disabled) .bx--tabs__nav-link, 
	.bx--tabs__nav-item--selected:not(.bx--tabs__nav-item--disabled) .bx--tabs__nav-link:focus, 
	.bx--tabs__nav-item--selected:not(.bx--tabs__nav-item--disabled) .bx--tabs__nav-link:active {
		border-color: $color-primary;
	}

	.bx--tabs--container .bx--tabs__nav-item--selected:not(.bx--tabs__nav-item--disabled) .bx--tabs__nav-link, 
	.bx--tabs--container .bx--tabs__nav-item--selected:hover:not(.bx--tabs__nav-item--disabled) .bx--tabs__nav-link {
		box-shadow: inset 0 2px 0 0 $color-primary;
	}

	.bx--tabs--container .bx--tabs__nav-item--selected:not(.bx--tabs__nav-item--disabled), 
	.bx--tabs--container .bx--tabs__nav-item--selected:hover:not(.bx--tabs__nav-item--disabled) {
		background-color: #fff;
	}

	.bx--tile-input:focus + .bx--tile,
	.bx--search-input:focus,
	.bx--select-input:focus,
	.bx--text-input:focus, 
	.bx--text-input:active,
	.bx--number input[type="number"]:focus,
	.bx--number input[type="number"]:focus ~ .bx--number__controls .bx--number__control-btn:hover,
	.bx--checkbox:focus + .bx--checkbox-label::before, 
	.bx--checkbox-label__focus::before, 
	.bx--checkbox:checked:focus + .bx--checkbox-label::before, 
	.bx--checkbox-label[data-contained-checkbox-state="true"].bx--checkbox-label__focus::before, 
	.bx--checkbox:indeterminate:focus + .bx--checkbox-label::before, 
	.bx--checkbox-label[data-contained-checkbox-state="mixed"].bx--checkbox-label__focus::before
	{
		outline: 2px solid $color-primary;
	}

	.bx--btn.bx--btn--icon-only.bx--tooltip__trigger:focus {
		border-color: $color-primary;
	}

	.bx--number__control-btn:focus {
		outline: 1px solid $color-primary;
	}

	.bx--toggle-input:focus + .bx--toggle-input__label > .bx--toggle__switch::before, 
	.bx--toggle-input:active + .bx--toggle-input__label > .bx--toggle__switch::before {
		box-shadow: 0 0 0 1px #fff, 0 0 0 3px $color-primary;
	}

	.bx--btn--ghost:focus {
		box-shadow: inset 0 0 0 1px $color-primary, inset 0 0 0 2px #fff;
	}

	.bx--toggle-input:checked + .bx--toggle-input__label > .bx--toggle__switch::before,
	.bx--progress-bar__bar {
		background-color: $color-primary;
	}

	.bx--progress-bar--indeterminate .bx--progress-bar__track::after {
		background-image: linear-gradient(90deg, $color-primary 12.5%, transparent 12.5%);
	}
	
	.bx--toggle-input__label .bx--toggle__switch {
		margin-top: .5rem;
	}

	.bx--slider__thumb:focus {
		background-color: $color-primary;
		box-shadow: inset 0 0 0 2px $color-primary, inset 0 0 0 3px #f4f4f4;
	}
	.bx--slider__thumb:focus ~ .bx--slider__filled-track {
		background-color: $color-primary;
	}

	.bx--accordion__heading:focus::before {
		border-color: $color-primary;
	}

	.save-form {
		display: grid;
		gap: 1rem;
		margin-bottom: 1.5rem;
	}

	.modal-content-wrapper {
		margin-bottom: 1.5rem;
	}

	.modal-text {
		p {
			margin: 0 0 1.5rem;
		}

		ul {
			font-size: 1rem;
			list-style: disc;
			padding-left: 2rem;
			margin: 0 0 1.5rem;
		}
	}

	header {
		height: 6rem;
		padding: .75rem 1rem;
		background-color: #fff;
		border-bottom: 2px solid #f0f0f0;
		display: flex;
		justify-content: space-between;
		align-items: center;

		img {
			height: 80%;
		}

		.header-buttons {
			display: flex;

			& > *:not(:last-child) {
				margin-right: .5rem;
			}
		}

		.banner {
			width: 768px;
			height: 90px;
			border: 0;
			overflow: hidden;

			@media(max-width: 1340px) {
				display: none;
			}
		}
	}

	main {
		display: grid;
		grid-template-columns: auto 410px;
		grid-template-rows: 100%;
		height: calc(100vh - 6rem);
		overflow: hidden;
	}

	.input-row {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 1rem;
		margin: 1.5rem 0;
	}

	.select-row {
		margin: 1.5rem 0;
	}

	.wallicon {
		display: inline-block;
		vertical-align: middle;
		width: 1rem;
		height: 1rem;
		border: 1px solid rgba(#000, .2);
		margin-right: .5rem;

		&--back {
			border-top: 1px solid #000;
			box-shadow: 0 -2px 0 0px #000;
		}

		&--left {
			border-top: 1px solid #000;
			border-left: 1px solid #000;
			box-shadow: -1px -1px 0 1px #000;
		}

		&--right {
			border-top: 1px solid #000;
			border-right: 1px solid #000;
			box-shadow: 1px -1px 0 1px #000;
		}

		&--u {
			border-top: 1px solid #000;
			border-left: 1px solid #000;
			border-right: 1px solid #000;
			box-shadow: -1px -1px 0 1px #000, 1px -1px 0 1px #000;
		}
	}
</style>